import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateBox } from "@babylonjs/core/Meshes/Builders/boxBuilder";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { ArenaBox, ArenaDefinition } from "@arena/shared";
import { segmentHitsBox } from "./geometry.ts";
import { ARENA_COLORS, hex } from "../palette.ts";
import { TEXELS_PER_METRE, surfaceRepeat, surfaceTexture, type SurfaceKind } from "./pixelTextures.ts";
import type { Hd2dWorld } from "./hd2d.ts";

/** "hd2d": pixel-art surfaces, the game's look (D33). "greybox": flat colours, the old prototype (`?look=3d`). */
export type ArenaStyle = "greybox" | "hd2d";

const EDGE_WIDTH = 0.35;
/** World size of one floor checker square, in metres. */
const TILE = 2;
/** Solids at least this tall can hide a fighter from the camera. */
const OCCLUDER_MIN_HEIGHT = 2;
const OCCLUDED_ALPHA = 0.3;
/** Fade slightly before the fighter is fully behind the solid. */
const OCCLUDER_PAD = 0.3;

interface Occluder {
  box: ArenaBox;
  material: StandardMaterial;
}

/**
 * Static meshes for an arena definition (data-driven, GDD §5–6). Built for
 * phones with up to 8 fighters on screen (D25): solids that share a material
 * are merged into one mesh (one draw call per material), static materials
 * are frozen, nothing static is pickable. Only the tall solids that can hide
 * a fighter stay separate, so each one can fade on its own.
 */
export class ArenaView {
  private readonly occluders: Occluder[] = [];
  /** Every static mesh: the HD-2D look casts and receives shadows with them. */
  readonly statics: Mesh[] = [];

  private readonly arena: ArenaDefinition;

  /** `drawn: false`: collisions only, another view draws the arena (the Colosseo's own look). */
  constructor(scene: Scene, arena: ArenaDefinition, style: ArenaStyle = "greybox", drawn = true) {
    this.arena = arena;
    const hd2d = style === "hd2d";
    if (!drawn) return;
    const shared = {
      floorSide: hd2d ? textured(scene, "floor-side", "wall", 8, 0.5) : flat(scene, "floor-side", ARENA_COLORS.floorSide),
      platform: hd2d ? textured(scene, "platform", "wood", 2, 1) : flat(scene, "platform", ARENA_COLORS.platform),
      edge: hd2d ? textured(scene, "edge", "trim", 40, 1) : flat(scene, "edge", ARENA_COLORS.edge),
    };
    const batches = new Map<StandardMaterial, Mesh[]>([
      [shared.floorSide, []],
      [shared.platform, []],
      [shared.edge, []],
    ]);

    for (const box of arena.solids) {
      const mesh = boxMesh(scene, box);
      if (box.kind === "floor") {
        batches.get(shared.floorSide)!.push(mesh);
        this.statics.push(hd2d ? buildTexturedTop(scene, box) : buildFloorTop(scene, box));
        if (!arena.theme) batches.get(shared.edge)!.push(...buildEdges(scene, box));
      } else if (box.max.y - box.min.y >= OCCLUDER_MIN_HEIGHT && !arena.theme) {
        // Own material, so each tall solid can fade on its own.
        const material = hd2d
          ? textured(scene, box.id, "brick", (box.max.x - box.min.x) / 2, (box.max.y - box.min.y) / 2)
          : flat(scene, box.id, box.kind === "wall" ? ARENA_COLORS.wall : ARENA_COLORS.pillar);
        mesh.material = material;
        mesh.isPickable = false;
        mesh.freezeWorldMatrix();
        this.statics.push(mesh);
        this.occluders.push({ box, material });
      } else {
        batches.get(shared.platform)!.push(mesh);
      }
    }

    for (const [material, meshes] of batches) {
      const merged = meshes.length > 1 ? Mesh.MergeMeshes(meshes, true, true) : meshes[0];
      if (!merged) continue;
      merged.material = material;
      merged.isPickable = false;
      merged.freezeWorldMatrix();
      this.statics.push(merged);
      // Frozen materials cannot pick up shadows and lights added later (HD-2D).
      if (!hd2d) material.freeze();
    }
  }

  /** HD-2D look: every static casts and receives shadows; a torch on the arena side of every pillar. */
  hd2dWorld(): Hd2dWorld {
    const torches: Vector3[] = [];
    for (const box of this.arena.solids.filter((b) => b.kind === "pillar")) {
      const cx = (box.min.x + box.max.x) / 2;
      const cz = (box.min.z + box.max.z) / 2;
      // The face of the pillar that looks at the arena centre.
      const towardsX = Math.abs(cx) > Math.abs(cz);
      const x = towardsX ? (cx > 0 ? box.min.x - 0.12 : box.max.x + 0.12) : cx;
      const z = towardsX ? cz : cz > 0 ? box.min.z - 0.12 : box.max.z + 0.12;
      torches.push(new Vector3(x, box.max.y - 0.4, z));
    }
    const floor = this.arena.solids.find((b) => b.kind === "floor");
    const extent = floor ? { x: floor.max.x, z: floor.max.z } : { x: 15, z: 10 };
    return { casters: this.statics, receivers: this.statics, torches, extent };
  }

  /** Fades tall solids that stand between the camera and the local fighter (GDD §6.1). */
  updateOcclusion(camera: Vector3, focus: Vector3 | null, dt: number): void {
    const blend = 1 - Math.exp(-dt * 12);
    for (const { box, material } of this.occluders) {
      const hidden = focus !== null && segmentHitsBox(camera, focus, box, OCCLUDER_PAD);
      const target = hidden ? OCCLUDED_ALPHA : 1;
      material.alpha += (target - material.alpha) * blend;
      if (Math.abs(material.alpha - target) < 0.01) material.alpha = target;
    }
  }
}

function boxMesh(scene: Scene, box: ArenaBox) {
  const mesh = CreateBox(
    box.id,
    { width: box.max.x - box.min.x, height: box.max.y - box.min.y, depth: box.max.z - box.min.z },
    scene,
  );
  mesh.position.set((box.min.x + box.max.x) / 2, (box.min.y + box.max.y) / 2, (box.min.z + box.max.z) / 2);
  return mesh;
}

/** Checkerboard top so motion and distance read clearly even at low resolution. */
function buildFloorTop(scene: Scene, box: ArenaBox): Mesh {
  const width = box.max.x - box.min.x;
  const depth = box.max.z - box.min.z;
  const ground = CreateGround(`${box.id}-top`, { width, height: depth }, scene);
  ground.position.set((box.min.x + box.max.x) / 2, box.max.y + 0.002, (box.min.z + box.max.z) / 2);

  const texture = new DynamicTexture(`${box.id}-checker`, { width: 2, height: 2 }, scene, false, Texture.NEAREST_SAMPLINGMODE);
  const ctx = texture.getContext();
  ctx.fillStyle = ARENA_COLORS.floorA;
  ctx.fillRect(0, 0, 2, 2);
  ctx.fillStyle = ARENA_COLORS.floorB;
  ctx.fillRect(0, 0, 1, 1);
  ctx.fillRect(1, 1, 1, 1);
  texture.update(false);
  texture.wrapU = Texture.WRAP_ADDRESSMODE;
  texture.wrapV = Texture.WRAP_ADDRESSMODE;
  texture.uScale = width / (TILE * 2);
  texture.vScale = depth / (TILE * 2);

  const material = new StandardMaterial(`${box.id}-top`, scene);
  material.diffuseTexture = texture;
  material.specularColor = Color3.Black();
  ground.material = material;
  ground.isPickable = false;
  ground.freezeWorldMatrix();
  material.freeze();
  return ground;
}

/** HD-2D floor: pixel flagstones, ~16 texels per metre. */
function buildTexturedTop(scene: Scene, box: ArenaBox): Mesh {
  const width = box.max.x - box.min.x;
  const depth = box.max.z - box.min.z;
  const ground = CreateGround(`${box.id}-top`, { width, height: depth }, scene);
  ground.position.set((box.min.x + box.max.x) / 2, box.max.y + 0.002, (box.min.z + box.max.z) / 2);
  const metres = 64 / TEXELS_PER_METRE;
  ground.material = textured(scene, `${box.id}-top`, "floor", width / metres, depth / metres);
  ground.isPickable = false;
  ground.freezeWorldMatrix();
  return ground;
}

function textured(scene: Scene, name: string, kind: SurfaceKind, uScale: number, vScale: number): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  const texture = surfaceTexture(scene, kind);
  texture.uScale = uScale * surfaceRepeat(kind);
  texture.vScale = vScale * surfaceRepeat(kind);
  material.diffuseTexture = texture;
  material.specularColor = Color3.Black();
  material.maxSimultaneousLights = 8;
  return material;
}

/** Warning stripes along the floor border: "the edge is here". */
function buildEdges(scene: Scene, box: ArenaBox): Mesh[] {
  const y = box.max.y + 0.004;
  const width = box.max.x - box.min.x;
  const depth = box.max.z - box.min.z;
  const strips = [
    { w: width, d: EDGE_WIDTH, x: (box.min.x + box.max.x) / 2, z: box.max.z - EDGE_WIDTH / 2 },
    { w: width, d: EDGE_WIDTH, x: (box.min.x + box.max.x) / 2, z: box.min.z + EDGE_WIDTH / 2 },
    { w: EDGE_WIDTH, d: depth, x: box.max.x - EDGE_WIDTH / 2, z: (box.min.z + box.max.z) / 2 },
    { w: EDGE_WIDTH, d: depth, x: box.min.x + EDGE_WIDTH / 2, z: (box.min.z + box.max.z) / 2 },
  ];
  return strips.map((s, i) => {
    const strip = CreateGround(`${box.id}-edge-${i}`, { width: s.w, height: s.d }, scene);
    strip.position.set(s.x, y, s.z);
    return strip;
  });
}

function flat(scene: Scene, name: string, color: string): StandardMaterial {
  const material = new StandardMaterial(name, scene);
  material.diffuseColor = hex(color);
  material.specularColor = Color3.Black();
  return material;
}
